import { Injectable } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { GraphsService } from './graphs.service';
import { Graph, GraphType, GaugeGraph, BarGraph, ExhaustGraph } from 'icreate/common/models/graphs';
import { printGaugeGraph } from './print-gauge-graph-utils';
import { SystemColorService } from '@service/system-color.service';
import { SystemColor } from 'icreate/common/models/system-color';
import { printBarGraph } from './print-bar-graph-utils';
import { printTurboChargerGraph } from './print-turbo-charger-graph-utils';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PullProjectService } from '@service/pull-project.service';
import { printExhaustGraph } from './print exhaust-graph-utils';
import { printDeviationGraph } from './print-deviation-graph-utils';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class PrintGraphsService {
    onPrintGraphs: Subject<void> = new Subject<void>();

    constructor(
        private graphService: GraphsService,
        private systemColorService: SystemColorService,
        private dialog: MatDialog,
        private pullService: PullProjectService
    ) {}

    async triggerPrint(vessel): Promise<boolean> {
        let loadingRef = null;

        try {
            const graphList = await this.graphService.getAll(vessel.id);

            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.GRAPHS,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    options: graphList,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;

            const { printType, version, iAPIShipyardlId, setListIds } = shouldPrint;
            if (!printType) return true;
            if ((setListIds as Set<string>).size === 0) return true;

            let graphsToPrint = graphList.filter(graph => setListIds.has(graph.id));
            let oldGraphs;

            if (printType === PrintType.UPDATES) {
                if (!iAPIShipyardlId || !version) return false;
                const iAPIVesselId = vessel.iAPIVesselId;
                oldGraphs = await this.pullService.pullGraphs(iAPIShipyardlId, iAPIVesselId, version);
            }

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 0 },
            });

            const [pdfMake, pdfFonts, pdfData] = await Promise.all([
                import('pdfmake/build/pdfmake'),
                import('pdfmake/build/vfs_fonts'),
                this.getPdfFile(graphsToPrint, vessel.id, oldGraphs, percent => {
                    if (loadingRef) {
                        loadingRef.componentInstance.data.progress = percent;
                    }
                }),
            ]);

            if (!pdfData) {
                if (loadingRef) loadingRef.close();
                return false;
            }

            const runtimeVfs = {
                ...pdfFonts.default?.pdfMake?.vfs,
            };

            const baseHref = window.location.origin + '/';

            const fonts = {
                MSGothic: {
                    normal: baseHref + 'assets/fonts/MSGothic.ttf',
                    bold: baseHref + 'assets/fonts/MSGothic.ttf',
                    italics: baseHref + 'assets/fonts/MSGothic.ttf',
                    bolditalics: baseHref + 'assets/fonts/MSGothic.ttf',
                },
            };

            const pdfDocGenerator = pdfMake.default.createPdf(pdfData, null, fonts, runtimeVfs);

            if (loadingRef) {
                loadingRef.close();
                loadingRef = null;
            }

            await new Promise<void>((resolve, reject) => {
                pdfDocGenerator.getBlob(blob => {
                    try {
                        const blobUrl = URL.createObjectURL(blob);
                        const newWindow = window.open(blobUrl, '_blank');
                        if (!newWindow) {
                            reject(new Error('Popup blocker prevented opening the new window.'));
                            return;
                        }
                        newWindow.onload = () => {
                            try {
                                newWindow.print();
                            } catch (e) {
                                console.error('Failed to trigger print dialog:', e);
                            }
                        };
                        resolve();

                        setTimeout(() => {
                            URL.revokeObjectURL(blobUrl);
                        }, 300000);
                    } catch (blobError) {
                        reject(blobError);
                    }
                });
            });

            return true;
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
        }
    }

    async getPdfFile(
        graphs: Graph[],
        vesselId,
        oldGraphs?: (GaugeGraph | BarGraph | ExhaustGraph)[],
        onProgress?: (percent: number) => void
    ) {
        if (graphs.length === 0) return;

        if (onProgress) onProgress(10);

        const graphsTypeMap: Map<string, Graph> = new Map();
        graphs.forEach(graph => graphsTypeMap.set(graph.id, graph));

        const graphPromises = graphs.map(graph => this.graphService.getGraphById(graph.id, graph.type));
        const fetchedGraphs = await Promise.all(graphPromises);
        const graphsToPrint = fetchedGraphs.filter(g => g !== undefined && g !== null);

        let systemColors = [];
        try {
            const res = await this.systemColorService.getAll(vesselId);
            systemColors = Array.isArray(res) ? res : res ? [res] : [];
        } catch (e) {
            console.error('System colors could not be loaded', e);
        }

        if (graphsToPrint.length === 0) return;

        const graphsWithType = graphsToPrint
            .map(graph => {
                const graphData = graphsTypeMap.get(graph.graphId);
                if (graphData) {
                    graph.type = graphData.type;
                    graph.title = graphData.title;
                    (graph as any).index = graphData.index;
                }
                return graph;
            })
            .sort((a: any, b: any) => a.index - b.index);

        if (onProgress) onProgress(30);

        return await this.preparePdf(graphsWithType, systemColors, oldGraphs, onProgress);
    }

    private async preparePdf(
        graphs,
        systemColors: SystemColor[],
        oldGraphs?: (GaugeGraph | BarGraph | ExhaustGraph)[],
        onProgress?: (percent: number) => void
    ) {
        const pdfContent = await this.generatePdfContent(graphs, systemColors, oldGraphs, onProgress);
        return {
            pageOrientation: 'landscape',
            pageSize: 'A4',
            content: pdfContent,
            info: { title: 'graphs' },
            defaultStyle: {
                fontSize: 9,
                color: '#000000',
                font: 'MSGothic',
            },
            pageMargins: [34.0157, 62.3622, 34.0157, 36.8504],
        };
    }

    private async generatePdfContent(
        graphs: (GaugeGraph | BarGraph | ExhaustGraph)[],
        systemColors: any,
        oldGraphs?: (GaugeGraph | BarGraph | ExhaustGraph)[],
        onProgress?: (percent: number) => void
    ) {
        const content = [];
        const echarts = await import('echarts');
        const totalGraphs = graphs.length;

        for (let i = 0; i < totalGraphs; i++) {
            if (onProgress) {
                const progressPercent = Math.round(30 + (i / totalGraphs) * 60);
                onProgress(progressPercent);
            }

            let oldGraph = null;
            const currentGraph = graphs[i];
            if (oldGraphs && Array.isArray(oldGraphs)) {
                oldGraph = oldGraphs.find(graph => graph && graph.id === currentGraph.graphId) || null;
            }

            const shouldBreak = i > 0;

            if (currentGraph.type === GraphType.Gauge) {
                const graphTable = await printGaugeGraph(currentGraph as GaugeGraph, echarts, systemColors, oldGraph);
                if (graphTable) {
                    content.push({
                        table: graphTable,
                        layout: this.getPdfLayout(),
                        ...(shouldBreak ? { pageBreak: 'before' } : {}),
                    });
                }
            } else if (currentGraph.type === GraphType.Bar) {
                const graphTable = await printBarGraph(currentGraph as BarGraph, echarts, oldGraph);
                if (graphTable) {
                    content.push({
                        table: graphTable,
                        layout: this.getPdfLayout(),
                        ...(shouldBreak ? { pageBreak: 'before' } : {}),
                    });
                }
            } else if (currentGraph.type === GraphType.Exhaust) {
                const exhaustData = await printExhaustGraph(currentGraph as ExhaustGraph, echarts, oldGraph);
                if (exhaustData && exhaustData.body) {
                    content.push({
                        table: {
                            body: exhaustData.body,
                            widths: exhaustData.widths || [],
                            heights: exhaustData.heights || [],
                        },
                        layout: this.getPdfLayout(),
                        ...(shouldBreak ? { pageBreak: 'before' } : {}),
                    });
                }
                const deviationData = await printDeviationGraph(currentGraph as ExhaustGraph, echarts, oldGraph);
                if (deviationData && deviationData.body) {
                    content.push({
                        pageBreak: 'before',
                        table: {
                            body: deviationData.body,
                            widths: deviationData.widths || [],
                            heights: deviationData.heights || [],
                        },
                        layout: this.getPdfLayout(),
                    });
                }

                const turboData = await printTurboChargerGraph(currentGraph as ExhaustGraph, echarts, oldGraph);
                if (turboData && turboData.body) {
                    content.push({
                        pageBreak: 'before',
                        table: {
                            body: turboData.body,
                            widths: turboData.widths || [],
                            heights: turboData.heights || [],
                        },
                        layout: this.getPdfLayout(),
                    });
                }
            }
        }

        if (onProgress) onProgress(100);
        return content;
    }

    private getPdfLayout() {
        return {
            paddingTop: () => 0,
            paddingRight: () => 0,
            paddingLeft: () => 0,
            paddingBottom: () => 0,
        };
    }
}
